export function getRoomKey(room: { id?: string; name?: string | null; roomKey?: string }, index: number) {
  return room.roomKey || room.id || `${room.name || 'room'}-${index}`;
}

export function getString(value: unknown) {
  return typeof value === 'string' || typeof value === 'number' ? String(value) : undefined;
}

export function hasUnsavedChanges<T, TComparable = T>(
  current: T,
  saved: T,
  normalize: (value: T) => TComparable = (value) => value as unknown as TComparable,
) {
  return JSON.stringify(normalize(current)) !== JSON.stringify(normalize(saved));
}

export interface PackageInclusionRecord {
  id?: string | number;
  asset_salesforce_id?: string | number;
  inclusion_id?: string | number;
  inclusion?: {
    category_id?: string | null;
    id?: string | number;
    id_salesforce_external?: string | number | null;
    name?: string;
    currency?: string;
    is_hidden?: boolean;
    requires_booking?: boolean;
    rrp?: number | null;
    type?: string;
    unit?: string;
    category?: {
      name?: string;
    };
  };
  category?: string | { name?: string };
  content?: string;
  name?: string;
  offer_id?: string | number;
  package_id?: string | number;
  package_name?: string;
  opportunity_name?: string;
  sort_order?: number;
  style?: string | null;
  lux_plus_tier?: string | null;
  vendor_salesforce_id?: string;
}

export function getInclusionCategory(inclusion: PackageInclusionRecord) {
  if (typeof inclusion.category === 'string') {
    return inclusion.category;
  }

  return inclusion.inclusion?.category?.name || inclusion.category?.name || '';
}

export function getInclusionName(inclusion: PackageInclusionRecord) {
  return inclusion.inclusion?.name || inclusion.name || inclusion.content || '';
}

export function getInclusionPersistId(inclusion: PackageInclusionRecord) {
  const persistId = inclusion.inclusion_id ?? inclusion.inclusion?.id;

  return persistId ? String(persistId) : undefined;
}

export function isCustomInclusion(inclusion: PackageInclusionRecord) {
  return !inclusion.inclusion?.id_salesforce_external;
}

// sort_order values at or above this mark a package-level manual override, not a master list position.
export const PACKAGE_OVERRIDE_SORT_ORDER_BASE = 5000;

export function getPackageInclusionSortOrder(inclusion: PackageInclusionRecord) {
  const sortOrder = Number(inclusion.sort_order);

  return Number.isInteger(sortOrder) && sortOrder > 0 ? sortOrder : undefined;
}

export function isPackageOverrideSortOrder(sortOrder: number | undefined): sortOrder is number {
  return sortOrder !== undefined && sortOrder >= PACKAGE_OVERRIDE_SORT_ORDER_BASE;
}

export function isInclusionHidden(inclusion: PackageInclusionRecord) {
  return !!inclusion.inclusion?.is_hidden;
}

export function updateInclusionCategory(inclusion: PackageInclusionRecord, category: string): PackageInclusionRecord {
  if (inclusion.inclusion) {
    return {
      ...inclusion,
      inclusion: {
        ...inclusion.inclusion,
        category: {
          ...inclusion.inclusion.category,
          name: category,
        },
      },
    };
  }

  return { ...inclusion, category };
}

export function updateInclusionName(inclusion: PackageInclusionRecord, name: string): PackageInclusionRecord {
  if (inclusion.inclusion) {
    return {
      ...inclusion,
      inclusion: {
        ...inclusion.inclusion,
        name,
      },
    };
  }

  return { ...inclusion, name };
}
